import React, { useState, useEffect, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import * as api from '../../../app/axios/api'
import { useRole } from '../../../context/RoleContext'
import {
  DollarSign,
  TrendingUp,
  AlertCircle,
  ChevronRight,
  Loader2, // 👈 added spinner icon
} from 'lucide-react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
  ResponsiveContainer,
  LineChart,
  Line,
  ReferenceLine,
} from 'recharts'

const Page = () => {
  const navigate = useNavigate()
  const { role } = useRole()

  const [dashboardDetails, setDashboardDetails] = useState({})
  const [fee, setFee] = useState([])
  const [expense, setExpense] = useState([])
  const [students, setStudents] = useState([])
  const [loading, setLoading] = useState(true) // 👈 loading state

  const currentDate = new Date()
  const [month, setMonth] = useState(currentDate.getMonth() + 1)
  const [year, setYear] = useState(currentDate.getFullYear())

  const colors = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', 'red', 'pink']

  // --- Stats calculation ---
  const { totalRevenue, totalExpenses, profitLoss } = useMemo(() => {
    const revenue = fee.reduce((sum, item) => sum + item.total_collected, 0)
    const expenses = expense.reduce((sum, item) => sum + item.total_expense, 0)
    return {
      totalRevenue: revenue,
      totalExpenses: expenses,
      profitLoss: revenue - expenses,
    }
  }, [fee, expense])

  const financialStatsCards =
    role === 'admin' || role === 'manager' || role === 'director'
      ? [
          {
            title: 'Monthly Revenue',
            value: `₹${totalRevenue.toLocaleString()}`,
            icon: DollarSign,
            color: 'bg-purple-500',
            changeType: 'positive',
          },
          {
            title: 'Monthly Expenses',
            value: `₹${totalExpenses.toLocaleString()}`,
            icon: TrendingUp,
            color: 'bg-yellow-500',
            route: '/expanse',
            changeType: 'neutral',
          },
          {
            title: 'Remaining Found',
            value: `₹${Math.abs(profitLoss).toLocaleString()}`,
            icon: AlertCircle,
            color: profitLoss >= 0 ? 'bg-green-500' : 'bg-red-500',
            changeType: profitLoss >= 0 ? 'positive' : 'negative',
          },
        ]
      : []

  const statsCardsToDisplay = [...financialStatsCards]

  // --- Chart Data ---
  const combinedProfitLossData = useMemo(() => {
    const dataMap = new Map()
    fee.forEach((item) => {
      const date = item.fee_date
      if (!dataMap.has(date))
        dataMap.set(date, { date, revenue: 0, expense: 0, profitLoss: 0 })
      dataMap.get(date).revenue += item.total_collected
    })
    expense.forEach((item) => {
      const date = item.e_date
      if (!dataMap.has(date))
        dataMap.set(date, { date, revenue: 0, expense: 0, profitLoss: 0 })
      dataMap.get(date).expense += item.total_expense
    })
    dataMap.forEach((v) => (v.profitLoss = v.revenue - v.expense))
    return Array.from(dataMap.values()).sort(
      (a, b) => new Date(a.date) - new Date(b.date),
    )
  }, [fee, expense])

  const isNextDisabled =
    month === currentDate.getMonth() + 1 && year === currentDate.getFullYear()

  // --- Fetch APIs ---
  const fetchAllData = async (month, year) => {
    try {
      setLoading(true)
      const [feesRes, expRes, dashRes, studentRes] = await Promise.all([
        api.fetchFeesDaysWise(month, year),
        api.fetchToExpense(month, year),
        api.fetchDashBoardDetails(),
        api.fetchDashStudent(month, year),
      ])
      setFee(feesRes.data)
      setExpense(expRes.data)
      setDashboardDetails(dashRes.data)
      setStudents(studentRes.data)
    } catch (error) {
      console.error(error)
      toast.error('Failed to load dashboard data.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAllData(month, year)
  }, [month, year])

  // --- Handlers ---
  const handlePrev = () => {
    if (month === 1) {
      setMonth(12)
      setYear((prev) => prev - 1)
    } else {
      setMonth((prev) => prev - 1)
    }
  }

  const handleNext = () => {
    if (!isNextDisabled) {
      if (month === 12) {
        setMonth(1)
        setYear((prev) => prev + 1)
      } else {
        setMonth((prev) => prev + 1)
      }
    }
  }

  // --- UI ---
  if (loading) {
    return (
      <div className='flex flex-col items-center justify-center h-[80vh] text-gray-700'>
        <Loader2 className='h-10 w-10 animate-spin text-blue-600 mb-3' />
        <p className='text-sm text-gray-500 animate-pulse'>
          Loading dashboard data...
        </p>
      </div>
    )
  }

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='bg-gradient-to-r from-blue-600 to-purple-600 p-6 rounded-xl text-white'>
        <h1 className='text-2xl font-bold mb-2'>Fee Management</h1>
        <p className='text-blue-100'>
          Unified Platform for Complete Institute Oversight - From Admissions to
          Analytics, Students to Success Metrics
        </p>
      </div>

      {/* Stats Grid */}
      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6'>
        {statsCardsToDisplay.map((card, index) => (
          <div
            key={index}
            className='bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer group'
            onClick={() => card.route && navigate(card.route)}
          >
            <div className='flex items-center justify-between'>
              <div>
                <p className='text-sm font-medium text-gray-600 mb-2'>
                  {card.title}
                </p>
                <p className='text-2xl font-bold text-gray-900'>{card.value}</p>
              </div>
              <div
                className={`${card.color} p-3 rounded-lg group-hover:scale-110 transition-transform duration-300`}
              >
                <card.icon className='h-6 w-6 text-white' />
              </div>
            </div>
            {card.route && (
              <div className='mt-4 flex items-center text-sm text-gray-500 group-hover:text-blue-600 transition-colors'>
                <span>View details</span>
                <ChevronRight className='h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform' />
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className='bg-white p-6 rounded-xl shadow-sm'>
        <h2 className='text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2'>
          <TrendingUp className='h-5 w-5' /> Analytics Overview
        </h2>

        <div className='flex justify-between items-center mb-6'>
          <button
            onClick={handlePrev}
            className='px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors'
          >
            Prev
          </button>
          <span className='text-lg font-semibold text-gray-700'>
            {new Date(year, month - 1).toLocaleString('default', {
              month: 'long',
              year: 'numeric',
            })}
          </span>
          <button
            onClick={handleNext}
            disabled={isNextDisabled}
            className={`px-4 py-2 rounded-lg transition-colors ${
              isNextDisabled
                ? 'bg-gray-300 cursor-not-allowed'
                : 'bg-blue-500 text-white hover:bg-blue-600'
            }`}
          >
            Next
          </button>
        </div>

        {/* Charts Grid */}
        <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
          {/* Daily Revenue */}
          <div className='p-6 bg-white rounded-xl shadow-sm'>
            <h3 className='text-lg font-bold text-gray-800 mb-4'>
              Daily Revenue
            </h3>
            <ResponsiveContainer width='100%' height={300}>
              <BarChart data={fee}>
                <CartesianGrid strokeDasharray='3 3' stroke='#e5e7eb' />
                <XAxis dataKey='fee_date' tick={{ fontSize: 12 }} />
                <YAxis />
                <Tooltip />
                <Bar
                  dataKey='total_collected'
                  fill='#6366f1'
                  radius={[6, 6, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Expense */}
          <div className='p-6 bg-white rounded-xl shadow-sm'>
            <h3 className='text-lg font-bold text-gray-800 mb-4'>
              Expense Overview
            </h3>
            <ResponsiveContainer width='100%' height={300}>
              <LineChart data={expense}>
                <CartesianGrid strokeDasharray='3 3' stroke='#e5e7eb' />
                <XAxis dataKey='e_date' tick={{ fontSize: 12 }} />
                <YAxis />
                <Tooltip />
                <Line
                  type='monotone'
                  dataKey='total_expense'
                  stroke='#ef4444'
                  strokeWidth={3}
                  dot={{ r: 4 }}
                  activeDot={{ r: 7 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Admissions */}
          <div className='p-6 bg-white rounded-xl shadow-sm'>
            <h3 className='text-lg font-bold text-gray-800 mb-4'>
              Total Admissions
            </h3>
            <ResponsiveContainer width='100%' height={300}>
              <BarChart data={students}>
                <CartesianGrid strokeDasharray='3 3' stroke='#e5e7eb' />
                <XAxis dataKey='admission_date' tick={{ fontSize: 12 }} />
                <YAxis />
                <Tooltip />
                <Bar dataKey='total_admissions' fill='#8884d8'>
                  {students.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={colors[index % colors.length]}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Profit/Loss */}
          <div className='p-6 bg-white rounded-xl shadow-sm'>
            <h3 className='text-lg font-bold text-gray-800 mb-4'>
              Daily Profit/Loss Overview
            </h3>
            <ResponsiveContainer width='100%' height={300}>
              <LineChart data={combinedProfitLossData}>
                <CartesianGrid strokeDasharray='3 3' stroke='#e5e7eb' />
                <XAxis dataKey='date' tick={{ fontSize: 12 }} />
                <YAxis />
                <Tooltip />
                <ReferenceLine y={0} stroke='#808080' strokeDasharray='3 3' />
                <Line
                  type='monotone'
                  dataKey='revenue'
                  stroke='#22c55e'
                  strokeWidth={2}
                />
                <Line
                  type='monotone'
                  dataKey='expense'
                  stroke='#f97316'
                  strokeWidth={2}
                />
                <Line
                  type='monotone'
                  dataKey='profitLoss'
                  stroke='#2563eb'
                  strokeWidth={3}
                  dot={{ r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Page
